/**
 * Why the section comparison is missing, in the operator's words (§0at).
 *
 * The belt hides a card with nothing to send — that rule is right and stays.
 * But "nothing to send" and "we could not look" are different states wearing
 * the same blank space, and the second one was costing the product its worst
 * demo moment: `/free-tools/convert-pdf-to-word` carries the largest demand
 * gap on its domain (9,059) and shows one internal link, because adobe.com
 * refused our crawler. The screen said the tool found nothing. The truth is
 * the tool was blocked, which is a fact about adobe rather than about the
 * page, and an operator who knows it stops trying to fix the page.
 *
 * Measured 2026-09-20: **61 of 782** coverage snapshots are
 * `rival_crawl_failed`, and they cluster on the tool and template pages where
 * the biggest gaps sit — because those pages' rivals are the sites most likely
 * to block a crawler (adobe.com, docs.google.com). The single largest gap on
 * each pilot domain is in this state.
 *
 * Deliberately silent on `ready` and `skipped`. A comparison that ran and
 * found nothing is the hide-the-card case, and saying "no gaps found" there
 * would put a reassurance on a surface that exists to hand off work.
 */
export type SectionCompareStatus = 'ready' | 'rival_crawl_failed' | 'eval_unavailable' | 'skipped';

export interface SectionCompareNotice {
  text: string;
  /** `blocked` is about their site; `unavailable` is about ours. Different fixes. */
  kind: 'blocked' | 'unavailable';
}

/** Strip protocol and `www.` — the operator reads a site, not a URL. */
function siteName(domain: string | null | undefined): string {
  return String(domain || '')
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .replace(/\/.*$/, '');
}

export function sectionCompareNotice(
  status: SectionCompareStatus | null | undefined,
  rivalDomain?: string | null
): SectionCompareNotice | null {
  const site = siteName(rivalDomain);
  switch (status) {
    case 'rival_crawl_failed':
      // Names the site, because the next move depends on who blocked us: a
      // one-off failure is worth retrying and adobe.com is not.
      return {
        kind: 'blocked',
        text: site
          ? `We could not read ${site} — their site blocked our crawler, so there is no comparison to show.`
          : 'We could not read the top result — their site blocked our crawler, so there is no comparison to show.'
      };
    case 'eval_unavailable':
      // Ours, not theirs (§0aj). It used to end "It will be retried", which
      // nothing did: cost-performance-index carried it from 1 Oct through a
      // full recrawl (findings §28). The build now retries before giving up, so
      // a row that reaches here has already been retried; say only what is true.
      return {
        kind: 'unavailable',
        text: site
          ? `We read ${site} but our analyser could not finish the comparison this time.`
          : 'We read the top result but our analyser could not finish the comparison this time.'
      };
    default:
      return null;
  }
}
